import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Input, Tag, Tooltip, message as antdMessage } from "antd";
import { useTranslation } from "react-i18next";
import {
    CheckOutlined,
    CloseOutlined,
    DislikeFilled,
    DislikeOutlined,
    LikeFilled,
    LikeOutlined,
    SendOutlined,
} from "@ant-design/icons";
import { createProductFeedback } from "../../../Services/feedback.service";
import { useFeedback } from "./FeedbackContext";
import {
    allowsMultiSelect,
    getPlaceholder,
    getReasons,
    serializeReasons,
} from "./feedbackReasons";
import "./feedback.css";

const { TextArea } = Input;

const BADGE_KEY_BY_FEATURE = {
    ai_comment: "feedback.badges.ai_comment",
    comment_details: "feedback.badges.comment_details",
    summary: "feedback.badges.summary",
    contract_profile: "feedback.badges.contract_profile",
    metadata: "feedback.badges.metadata",
    analysis_stream: "feedback.badges.analysis_stream",
    export: "feedback.badges.export",
};

const FeedbackDrawer = () => {
    const { t } = useTranslation("review");
    const { open, draft, closeFeedback, analyseId, documentVersionId, workspaceId } = useFeedback();

    const [sentiment, setSentiment] = useState(null);
    const [selectedReasons, setSelectedReasons] = useState([]);
    const [userMessage, setUserMessage] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const textareaRef = useRef(null);

    const featureArea = draft?.featureArea || "global_review";
    const targetType = draft?.targetType || "review_page";
    const targetId = draft?.targetId || null;
    const aiCommentId = draft?.aiCommentId || null;
    const presetContext = draft?.contextJson || null;
    const commentType = presetContext?.comment_type || null;
    const badgeKey = BADGE_KEY_BY_FEATURE[featureArea] || null;
    const badgeLabel = draft?.badgeLabel || (badgeKey ? t(badgeKey) : null);
    const multiSelect = allowsMultiSelect(featureArea);

    // Reset à chaque ouverture, pré-coche presetReason si fournie.
    useEffect(() => {
        if (!open) return;
        setSentiment(null);
        const preset = draft?.presetReason;
        setSelectedReasons(preset ? [preset] : []);
        setUserMessage("");
        setSubmitting(false);
        setSent(false);
    }, [open, draft?.presetReason]);

    // Focus textarea après que les raisons soient révélées (sentiment choisi).
    useEffect(() => {
        if (!open || !sentiment) return;
        const id = window.setTimeout(() => {
            try { textareaRef.current?.focus?.({ cursor: "end" }); } catch { /* noop */ }
        }, 260);
        return () => window.clearTimeout(id);
    }, [open, sentiment]);

    const reasons = useMemo(
        () => getReasons({ featureArea, sentiment, commentType }),
        [featureArea, sentiment, commentType]
    );

    const placeholder = useMemo(
        () => getPlaceholder({ featureArea, sentiment, commentType }),
        [featureArea, sentiment, commentType]
    );

    const close = () => closeFeedback();

    const toggleReason = (reason) => {
        setSelectedReasons((prev) => {
            const isSelected = prev.includes(reason);
            if (multiSelect) {
                return isSelected ? prev.filter((r) => r !== reason) : [...prev, reason];
            }
            return isSelected ? [] : [reason];
        });
    };

    const submitFeedback = async () => {
        if (!sentiment || sent || submitting) return;
        setSubmitting(true);
        try {
            await createProductFeedback({
                page_route: `${window.location.pathname}${window.location.search || ""}`,
                workspace_id: workspaceId,
                analyse_id: analyseId,
                document_version_id: documentVersionId,
                ai_comment_id: aiCommentId,
                feature_area: featureArea,
                target_type: targetType,
                target_id: targetId,
                interaction_type: "manual_drawer",
                reason: serializeReasons(selectedReasons),
                sentiment,
                message: userMessage,
                context_json: {
                    source: presetContext?.source || "feedback_drawer",
                    feature_area: featureArea,
                    target_type: targetType,
                    target_id: targetId,
                    ...(presetContext || {}),
                    // Liste détaillée pour analytics, sans casser le champ `reason`.
                    selected_reasons: selectedReasons,
                },
            });
            setSent(true);
            antdMessage.success({ content: t("feedback.success"), duration: 1.6 });
            window.setTimeout(close, 850);
        } catch {
            antdMessage.warning(t("feedback.error"));
            setSubmitting(false);
        }
    };

    const renderThumb = (value, { Outline, Filled, label, hint }) => {
        const active = sentiment === value;
        return (
            <button
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={label}
                className={`feedback-thumb feedback-thumb--${value} ${active ? "is-active" : ""}`}
                onClick={() => setSentiment(active ? null : value)}
            >
                <span className="feedback-thumb__icon" aria-hidden="true">
                    {active ? <Filled /> : <Outline />}
                </span>
                <span className="feedback-thumb__label">{label}</span>
                <span className="feedback-thumb__hint">{hint}</span>
            </button>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={close}
            placement="right"
            width={440}
            closable={false}
            destroyOnClose
            mask={false}
            keyboard
            className="feedback-drawer"
            styles={{ body: { padding: 0 } }}
        >
            <div className="feedback-panel">
                <header className="feedback-panel__header">
                    <div className="feedback-panel__heading">
                        <h3 className="feedback-panel__title">{t("feedback.title")}</h3>
                        <p className="feedback-panel__subtitle">
                            {t("feedback.subtitle")}
                        </p>
                        {badgeLabel && (
                            <span className="feedback-panel__context-badge">{badgeLabel}</span>
                        )}
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={close}
                        className="feedback-panel__close"
                        aria-label={t("feedback.close")}
                    />
                </header>

                {/* Étape 1 — Thumbs (sentiment-first) */}
                <section className="feedback-panel__section feedback-panel__section--thumbs">
                    <div className="feedback-panel__label">{t("feedback.question")}</div>
                    <div className="feedback-thumbs" role="radiogroup" aria-label={t("feedback.sentimentAria")}>
                        {renderThumb("positive", {
                            Outline: LikeOutlined,
                            Filled: LikeFilled,
                            label: t("feedback.positiveLabel"),
                            hint: t("feedback.positiveHint"),
                        })}
                        {renderThumb("negative", {
                            Outline: DislikeOutlined,
                            Filled: DislikeFilled,
                            label: t("feedback.negativeLabel"),
                            hint: t("feedback.negativeHint"),
                        })}
                    </div>
                </section>

                {/* Étape 2 — Raisons contextuelles (révélées après thumb) */}
                {sentiment && reasons.length > 0 && (
                    <section className="feedback-panel__section feedback-panel__section--reasons">
                        <div className="feedback-panel__label">
                            <span>
                                {sentiment === "positive"
                                    ? t("feedback.positiveReasonQuestion")
                                    : t("feedback.negativeReasonQuestion")}
                            </span>
                            {multiSelect && (
                                <Tooltip title={t("feedback.multiSelectTooltip")}>
                                    <span className="feedback-panel__label-hint">{t("feedback.multiSelect")}</span>
                                </Tooltip>
                            )}
                        </div>
                        <div className="feedback-reasons">
                            {reasons.map((item, idx) => (
                                <Tag.CheckableTag
                                    key={item}
                                    checked={selectedReasons.includes(item)}
                                    onChange={() => toggleReason(item)}
                                    className={`feedback-reason feedback-reason--${sentiment}`}
                                    style={{ animationDelay: `${idx * 28}ms` }}
                                >
                                    {item}
                                </Tag.CheckableTag>
                            ))}
                        </div>
                    </section>
                )}

                {/* Étape 3 — Message libre */}
                <section
                    className={`feedback-panel__section feedback-panel__section--grow ${sentiment ? "" : "is-dimmed"}`}
                >
                    <div className="feedback-panel__label">{t("feedback.details")}</div>
                    <TextArea
                        ref={textareaRef}
                        value={userMessage}
                        onChange={(event) => setUserMessage(event.target.value)}
                        placeholder={placeholder}
                        autoSize={{ minRows: 4, maxRows: 8 }}
                        maxLength={1200}
                        showCount
                        className="feedback-textarea"
                    />
                </section>

                <footer className="feedback-panel__footer">
                    <Button onClick={close} type="text" className="feedback-panel__secondary">
                        {t("feedback.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        icon={sent ? <CheckOutlined /> : <SendOutlined />}
                        loading={submitting}
                        disabled={!sentiment || sent}
                        onClick={submitFeedback}
                        className={`feedback-panel__submit ${sent ? "is-sent" : ""}`}
                    >
                        {sent ? t("feedback.sent") : t("feedback.send")}
                    </Button>
                </footer>
            </div>
        </Drawer>
    );
};

export default FeedbackDrawer;
